import React, { ReactElement } from 'react';
import classnames from 'classnames';
import Modal, { ModalProps } from 'react-bootstrap/Modal';
import { Form } from 'react-bootstrap';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import styles from './FormModal.module.scss';
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  UseFormReturn,
} from 'react-hook-form';

interface FormModalProps<FormFields extends FieldValues> extends ModalProps {
  show?: boolean;
  onClose?: () => void;
  onCancel?: () => void;
  onSubmit: SubmitHandler<FormFields>;
  shouldResetForm?: boolean;
  title: string;
  cancelBtnText?: string;
  okBtnText?: string;
  className?: string;
  isLoading?: boolean;
  children: ReactElement | ReactElement[];
  footer?: ReactElement | ReactElement[] | null;
  header?: ReactElement | ReactElement[] | null;
  formContext: UseFormReturn<FormFields>;
  showFooter?: boolean;
  showCloseButton?: boolean;
}

const FormModal = <FormFields extends FieldValues>({
  show = true,
  onClose,
  onCancel,
  onSubmit,
  title,
  cancelBtnText = 'Cancel',
  okBtnText = 'Save',
  className,
  children,
  formContext,
  isLoading,
  footer,
  header,
  showCloseButton = false,
  showFooter = true,
  shouldResetForm = true,
  ...modalProps
}: FormModalProps<FormFields>) => {
  const {
    reset: resetForm,
    formState: { isValid: isFormValid },
    handleSubmit,
    control: { _fields },
  } = formContext;

  const handleOnEntered = () => {
    // focus first input
    Object.values(_fields)[0]?._f?.ref?.focus?.();
  };

  const handleOnClose = () => {
    shouldResetForm && resetForm();
    onClose?.();
  };

  const handleOnCancel = () => {
    shouldResetForm && resetForm();
    onCancel?.();
  };

  const submitDisabled = isLoading || !isFormValid;

  return (
    <Modal
      data-cy="modal"
      show={show}
      className={classnames(className, styles.modal)}
      centered={true}
      animation={true}
      onHide={handleOnClose}
      onExited={handleOnClose}
      onEntered={handleOnEntered}
      {...modalProps}
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header
          className={classnames('align-items-center')}
          closeButton={showCloseButton}
        >
          <Modal.Title data-cy="modal-title">{header || title}</Modal.Title>
        </Modal.Header>

        <Modal.Body className={classnames(styles.formBody)}>
          <FormProvider {...formContext}>{children}</FormProvider>
        </Modal.Body>

        {showFooter && (
          <Modal.Footer>
            {footer || (
              <>
                <button
                  className={classnames(
                    'btn btn-primary-outline btn-secondary'
                  )}
                  onClick={onCancel ? handleOnCancel : handleOnClose}
                  type="button"
                >
                  {cancelBtnText}
                </button>
                <button
                  className="btn btn-primary ml-auto"
                  disabled={submitDisabled}
                  type="submit"
                  data-cy="confirmation-modal:confirmation-modal-ok-button"
                >
                  {isLoading ? (
                    <LoadingIndicator className={styles.loadingIndicator} />
                  ) : (
                    okBtnText
                  )}
                </button>
              </>
            )}
          </Modal.Footer>
        )}
      </Form>
    </Modal>
  );
};

export default FormModal;
